import React, {
  Children,
  useContext,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { Column, FlexBox, FlexBoxType, Row } from "../../components/FlexBox";
import { KyrButton } from "../../components/KyrButton";
import { KyrHandleQuery } from "../../components/KyrHandleQuery";
import { KyrCancelTreeUpdatePopUp } from "../../components/KyrPopUp";
import { KyrSubTitle } from "../../components/KyrSubTitle";
import { KyrTitle } from "../../components/KyrTitle";
import { StatusToUpdateContext, TreeContext, TreeListContext } from "../../contexts/organizeTreeBibleContexts";
import { LockContext } from "../../contexts/treeLockContext";
import '../../globalStyle.css';
import { useSetRootHeightTo100vh } from "../../hooks/UIhooks";
import { useHandleSearchChange } from "../../hooks/searchHooks";
import { useHandleSaveTree } from "../../hooks/treeHooks";
import { useReleaseLock, useTreeLockLifecycle } from "../../hooks/treeLockHooks";
import { useFetchDataOnMount } from "../../hooks/useFetchDataOnMount";
import { ChildrenProps } from "../../types";
import { borderWith, box, height, marginBottom } from "./styles";
import lockLogo from "../../logo/lock_logo.png";
import { UDTank } from "./tank/UDTank";
import { Tree } from "./tree/Tree";


const TopLineContainer = (props: FlexBoxType & { direction: 'row' | 'column' }) => {
  return (
    <FlexBox
      {...props}
      style={{ flexDirection: props.direction }}
      height={height}
      paddingLeft={borderWith}
      marginBottom={marginBottom}
    />
  )
}

const TreeLabelAndButtonsLayout = ({ children }: ChildrenProps) => {
  const childrenList = Children.toArray(children)

  return (
    <TopLineContainer
      direction="row"
      justifyContent="space-between"
      alignItems="flex-end"
    >
      {childrenList[0]}
      <DoubleButtonsRow>
        {childrenList[1]}
        {childrenList[2]}
      </DoubleButtonsRow>
    </TopLineContainer>
  )
}

const DoubleButtonsRow = ({ children }: ChildrenProps) => {
  const childrenList = Children.toArray(children)
  return (
    <Row colGap={7} >
      {childrenList[0]}
      {childrenList[1]}
    </Row>
  )
}

const Layout = ({ children }: ChildrenProps) => {
  const childrenList = Children.toArray(children)

  const udTankWidth = 45
  const treeBoxWidth = 99 - udTankWidth
  return (
    <>
      <Row
        id="organize-tree-global-container"
        grow={1}
        justifyContent="space-between"
        overflow="auto"
        padding="20px"
      >
        <Column width={`${udTankWidth}%`}>
          <TopLineContainer
            direction="column"
            justifyContent="space-between"
          >
            {childrenList[0]}
          </TopLineContainer>
          <Column style={box}>
            {childrenList[1]}
          </Column>
        </Column>
        <Column width={`${treeBoxWidth}%`}>
          {childrenList[2]}
          <Column grow={1} style={box}>
            {childrenList[3]}
          </Column>
        </Column>
      </Row>
    </>
  )
}

const UDTankLabel = ({ currentTree }: { currentTree?: string }) => {
  const {t} = useTranslation()
  return (
  <>
    <KyrTitle>{t('kyrielle.organizeTree.title', {cdfTitle: currentTree ? t(`kyrielle.organizeTree.cdfTitle.${currentTree}`) : '...'})}</KyrTitle>
    <KyrSubTitle>{t('kyrielle.organizeTree.elementsToInsert')}</KyrSubTitle>
  </>
)}

const LockBanner = () => {
  const { t } = useTranslation()
  return (
    <Row
      alignItems="center"
      colGap={8}
    >
      
      <Row alignItems="center" colGap={8}>
        <img src={lockLogo} height="14" width="14" alt="verrou" />
        <KyrSubTitle>{t('kyrielle.organizeTree.lock.lockedBy')}</KyrSubTitle>
        <span
        onClick={() => window.location.reload()}
        style={{ color: '#ffbe00', cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
        >
          {t('kyrielle.organizeTree.lock.refresh')}
        </span>
      </Row>
      
    </Row>
  )
}

export const TreeLabelAndButtons = ({ setShow }: { setShow: Function }) => {
  const [tree] = useContext(TreeContext)
  const [lockState] = useContext(LockContext)
  const handleSearch = useHandleSearchChange()
  const handleSave = useHandleSaveTree()
  const releaseLock = useReleaseLock()
  const {t} = useTranslation()
  const [, setStatusToUpdate] = useContext(StatusToUpdateContext)

  const handleClickOnSaveButton = async () => {
    const result = await handleSave()
    if (result !== undefined) {
      await releaseLock()
    }
    setStatusToUpdate({})
    await handleSearch()
  }

  const handleClickOnCancelButton = () => {
    setShow(true)
  }

  return (
    <TreeLabelAndButtonsLayout>
      <Row alignItems="center" colGap={4}>
        <KyrSubTitle>{t('kyrielle.organizeTree.cdf')}</KyrSubTitle>
      </Row>
      {lockState.status === "other" ? (
        <LockBanner />
      ) : (
        <KyrButton
          disabled={!tree.data?.isDirty}
          onClick={handleClickOnCancelButton}
          kyrType="secondary">
          {t('kyrielle.button.cancel')}
        </KyrButton>
      )}
      {lockState.status === "other" ? null : (
        <KyrButton
          disabled={!tree.data?.isDirty || lockState.status !== "mine"}
          onClick={handleClickOnSaveButton}
          kyrType="primary">
          {t('kyrielle.button.save')}
        </KyrButton>
      )}
    </TreeLabelAndButtonsLayout>
  )
}

export const OrganizeTree = () => {

  useSetRootHeightTo100vh()
  useTreeLockLifecycle()

  const [show, setShow] = useState(false)
  const [tree] = useContext(TreeContext)

  const [treeList, setTreeList] = useFetchDataOnMount({
    url: '/api/kyrielle/tree',
    method: 'get',
    context: TreeListContext,
  })
  
  return (
    <KyrHandleQuery errorMessage="KYRIELLE_TREE_LIST_LOAD" query={treeList} setQuery={setTreeList}>
      <KyrCancelTreeUpdatePopUp showState={[show, setShow]} />
      <Layout>
        <UDTankLabel currentTree={tree.data?.type} />
        <UDTank />
        <TreeLabelAndButtons setShow={setShow} />
        <Tree />
      </Layout>
    </KyrHandleQuery>
  )
}
